
import React, { useEffect, useRef } from 'react';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';
import { apiSpecification } from './ApiSpecification';
import { apiPaths } from './ApiPathsRegistry';

interface SwaggerUIContainerProps {
  apiKey: string;
  docExpanded?: boolean;
}

export default function SwaggerUIContainer({ apiKey, docExpanded = true }: SwaggerUIContainerProps) {
  const swaggerRef = useRef<HTMLDivElement>(null);
  
  // Merge apiPaths into the specification
  const spec = {
    ...apiSpecification,
    paths: apiPaths
  };

  useEffect(() => {
    // Add custom CSS to fix SwaggerUI styling issues
    const style = document.createElement('style');
    style.textContent = `
      .swagger-ui .info {
        margin: 20px 0;
      }
      .swagger-ui .scheme-container {
        padding: 15px 0;
        background: none;
        box-shadow: none;
      }
      .swagger-ui section.models {
        margin-top: 20px;
      }
      .swagger-ui .opblock {
        margin-bottom: 15px;
        border-radius: 6px;
      }
      .swagger-ui .opblock-tag {
        font-size: 18px;
        margin-top: 16px;
      }
      .swagger-ui table tbody tr td {
        padding: 8px 0;
      }
      .swagger-ui .response-col_status {
        font-size: 14px;
      }
      .swagger-ui section.models .model-container {
        margin: 0 0 15px;
      }
      .swagger-ui .authorization__btn {
        margin-right: 8px;
      }
      /* Hide the sidebar */
      .swagger-ui .wrapper {
        max-width: none;
        padding: 0;
      }
      /* Make the content full width */
      .swagger-ui .swagger-ui-wrap {
        max-width: none;
      }
      /* Completely hide Models section */
      .swagger-ui section.models {
        display: none;
      }
      /* Hide the right-side information block */
      .swagger-ui .information-container {
        padding: 0;
      }
      /* Hide the sidebar completely */
      .swagger-ui .opblock-tag-section {
        width: 100%;
      }
      /* Remove any split view */
      .swagger-ui .split-pane {
        display: block !important;
      }
      /* Hide any side navigation */
      .swagger-ui .sidebar {
        display: none !important;
      }
      /* Ensure operations take full width */
      .swagger-ui .opblock-body {
        width: 100%;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    // Set the API key in the auth button when it changes
    if (apiKey && swaggerRef.current) {
      const authButton = swaggerRef.current.querySelector('.authorize') as HTMLElement;
      if (authButton) {
        authButton.click();
        
        // Wait for the modal to appear
        setTimeout(() => {
          const input = document.querySelector('input[placeholder="JWT"]') as HTMLInputElement;
          if (input) {
            input.value = apiKey;
            
            // Trigger input event to update the SwaggerUI state
            const event = new Event('input', { bubbles: true });
            input.dispatchEvent(event);
            
            // Find and click the Authorize button
            const authorizeButton = Array.from(document.querySelectorAll('button')).find(
              button => button.textContent === 'Authorize'
            ) as HTMLButtonElement;
            
            if (authorizeButton) {
              authorizeButton.click();
              
              // Find and click the Close button
              const closeButton = Array.from(document.querySelectorAll('button')).find(
                button => button.textContent === 'Close'
              ) as HTMLButtonElement;
              
              if (closeButton) {
                closeButton.click();
              }
            }
          }
        }, 100);
      }
    }
  }, [apiKey]);

  // Set expanded or collapsed state for all endpoints
  useEffect(() => {
    if (swaggerRef.current) {
      setTimeout(() => {
        const tagElements = swaggerRef.current?.querySelectorAll('.opblock-tag');
        tagElements?.forEach((tag) => {
          const currentState = (tag as HTMLElement).getAttribute('data-is-open') === 'true';
          if ((docExpanded && !currentState) || (!docExpanded && currentState)) {
            (tag as HTMLElement).click();
          }
        });
      }, 500);
    }
  }, [docExpanded]);

  return (
    <div ref={swaggerRef} className="swagger-ui-container p-4">
      <SwaggerUI
        spec={spec}
        docExpansion={docExpanded ? 'list' : 'none'}
        defaultModelsExpandDepth={docExpanded ? 1 : 0}
        layout="BaseLayout"
        displayRequestDuration={true}
        filter={true}
        deepLinking={true}
        supportedSubmitMethods={["get", "post", "put", "delete", "patch"]}
      />
    </div>
  );
}
